import {
  formatInstant,
  formatPlainDate,
  FrequentTimeZones,
  isAirAdmin,
  safeInstantFromString,
} from '@airreading/common';
import {
  adjustMinimalClassTimeAcrossTZ,
  EditStudentGroupBulkActions,
  GetStudentGroupsSortableColumn,
  StudentGroupReassignmentRequestStatus,
  StudentGroupStatus,
  StudentGroupWithScheduleAndLevel,
  StudentYear,
  StudentYearColorMap,
  getStudentGroupsSortOrderSchema,
  getStudentGroupsSortableColumnSchema,
  studentYearOption,
  studentYearSchema,
  ClassTimeType,
  studentGroupTypeSchema,
} from '@airreading/common/data';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { UserPenIcon } from 'lucide-react';
import { useMemo } from 'react';
import { z } from 'zod';

import { AirIdDisplay } from '@/components/air-id-display.js';
import { InfoPage } from '@/components/info-page.js';
import { Button } from '@/components/ui/button.js';
import { createTableRowSelectionCell, EntityTable } from '@/components/table/entity-table.js';
import { TableErrorState } from '@/components/table/table-error-state.js';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown.js';
import { TableFilterInputField } from '@/components/table/table-filter-field.js';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button.js';
import { Badge } from '@/components/ui/badge.js';
import { EmptyArray, getStudentGroups } from '@/lib/api-calls.js';
import { useRowsBulkSelection } from '@/lib/bulk-selection.js';
import { useOrgOptions, useSchoolAndOrgOptions } from '@/lib/api-call-hooks.js';
import { getCoachOverallGradeColor, getCoachOverallGradeLabel } from '@/lib/coach-utils.js';
import { useErrorToast } from '@/lib/error-handling.js';
import { usePaginatedFilters } from '@/lib/filtering.js';
import { studentGroupTypesFilterOption } from '@/lib/form-utils.js';
import { paginationRequestSchema, DEFAULT_PAGE_SIZE } from '@/lib/pagination.js';
import { useNavigateSearchState, validateRouteRole } from '@/lib/routing.js';
import { useSorting } from '@/lib/sorting.js';
import { getStudentYearOptions } from '@/lib/student-utils.js';
import { airColumnHelper } from '@/lib/tables.js';
import { formatStudentClassTimeAbbr } from '@/routes/_layout/scheduling/schedules/-components/formatting.js';
import { ClassSessionsDrawer } from '@/routes/_layout/orgs/schools/-components/class-sessions-drawer/index.js';
import { BulkUpdateStudentGroups } from '@/routes/_layout/student-groups/-components/bulk-update-student-groups.js';
import { StudentGroupReassignmentRequestModal } from '@/routes/_layout/student-groups/-components/student-group-reassignment-request-modal.js';
import { DeleteStudentGroup } from '@/routes/_layout/orgs/schools/-components/delete-student-group.js';
import { PeopleBadgeList } from '@/routes/_layout/scheduling/student-groups/-components/people-badge-list.js';
import { studentGroupsTabs } from '@/routes/_layout/student-groups/-components/tabs.js';

const studentGroupsSearchSchema = paginationRequestSchema.extend({
  query: z.string().optional().catch(''),
  scheduleSchools: z.array(z.string()).optional(),
  scheduleOrgs: z.array(z.string()).optional(),
  grades: z.array(studentYearSchema).optional(),
  types: z.array(studentGroupTypeSchema).optional(),
  sortBy: getStudentGroupsSortOrderSchema.optional(),
});

export const Route = createFileRoute('/_layout/student-groups/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: StudentGroupsIndex,
  validateSearch: studentGroupsSearchSchema,
});

const col = airColumnHelper<StudentGroupWithScheduleAndLevel>(getStudentGroupsSortableColumnSchema);

const columns = [
  createTableRowSelectionCell(col),

  col.accessor((row) => row, {
    id: 'reassignment-request',
    header: '',
    size: 50,
    cell: ({ getValue }) => {
      const studentGroup = getValue();
      const hasRequestedReassignment = studentGroup.reassignmentRequests.some(
        (request) => request.displayStatus === StudentGroupReassignmentRequestStatus.requested,
      );

      return (
        <StudentGroupReassignmentRequestModal
          studentGroup={studentGroup}
          trigger={
            <Button variant="ghost" size="icon" aria-label="Request reassignment" disabled={hasRequestedReassignment}>
              <UserPenIcon size={18} />
            </Button>
          }
        />
      );
    },
  }),

  col.accessor((row) => row, {
    id: 'classId',
    header: 'Class ID',
    cell: ({ getValue }) => {
      const studentGroup = getValue();
      return (
        <AirIdDisplay
          entityType="cls"
          rawId={studentGroup.id}
          wrapper={(displayId) => (
            <ClassSessionsDrawer
              trigger={
                <Button variant="link" className="p-0 h-auto text-blue-600 hover:text-blue-800 no-underline">
                  {displayId}
                </Button>
              }
              classId={studentGroup.id}
            />
          )}
        />
      );
    },
  }),

  col.accessor((row) => row, {
    id: 'reassignments',
    header: 'Reassignments',
    cell: ({ getValue }) => {
      const studentGroup = getValue();
      const { classTime, reassignmentRequests } = studentGroup;
      const schoolId = classTime.schedule.school.id;
      const classDetailPath =
        classTime.type === ClassTimeType.one_time
          ? '/orgs/schools/$schoolId/one-time-classes/$classId'
          : '/orgs/schools/$schoolId/classes/$classId';

      return (
        <Link
          to={classDetailPath}
          params={{ schoolId, classId: studentGroup.id }}
          className="text-blue-600 hover:underline"
        >
          {reassignmentRequests.length}
        </Link>
      );
    },
  }),

  col.accessor((row) => row, {
    id: 'school',
    header: 'School - District',
    cell: ({ getValue }) => {
      const { classTime } = getValue();
      const { school } = classTime.schedule;
      const schoolClassesPath =
        classTime.type === ClassTimeType.one_time
          ? '/orgs/schools/$schoolId/one-time-classes'
          : '/orgs/schools/$schoolId/classes';

      return (
        <Link
          to={schoolClassesPath}
          params={{ schoolId: school.id }}
          search={{ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE }}
          className="text-blue-600 text-nowrap hover:underline"
        >
          {school.name} – {school.org.name}
        </Link>
      );
    },
  }),

  col.accessor('coach', {
    id: 'coach',
    header: 'Teacher',
    cell: ({ getValue }) => {
      const coach = getValue();
      return coach ? <Badge variant="outline">{coach.fullNameOrEmail}</Badge> : <span>No teacher assigned</span>;
    },
  }),

  col.accessor((row) => row, {
    id: 'overallGrade',
    header: 'Overall grade',
    cell: ({ getValue }) => {
      const { coach } = getValue();
      const overallGrade = coach?.coachProfile.overallGrade;
      const color = getCoachOverallGradeColor(overallGrade);
      if (color) {
        return (
          <span
            className="inline-flex h-7 w-7 items-center justify-center rounded-full text-xl font-bold text-white"
            style={{ backgroundColor: color }}
          >
            {getCoachOverallGradeLabel(overallGrade)}
          </span>
        );
      }
      return getCoachOverallGradeLabel(overallGrade);
    },
  }),

  col.accessor('students', {
    id: 'students',
    header: 'Students',
    cell: ({ getValue }) => <PeopleBadgeList people={getValue()} wrap={true} />,
  }),

  col.accessor('grades', {
    id: 'grades',
    header: 'Student grade',
    cell: ({ getValue }) => {
      const grades = getValue();
      if (!grades || grades.length === 0) {
        return <span>-</span>;
      }

      return (
        <div className="flex flex-wrap gap-1">
          {grades.map((grade, index) => {
            const studentYear = grade as StudentYear;
            if (!studentYear) {
              return null;
            }
            return (
              <span
                key={index}
                className="min-w-9 rounded-full px-2 py-1 text-center text-xs"
                style={{ backgroundColor: StudentYearColorMap[studentYear] }}
              >
                {studentYearOption(studentYear)}
              </span>
            );
          })}
        </div>
      );
    },
  }),

  col.accessor('classTime', {
    id: 'schedule',
    header: 'Schedule',
    cell: ({ getValue }) => {
      const { schedule } = getValue();
      return (
        <Badge variant="outline" className="text-nowrap">
          {schedule.name}
        </Badge>
      );
    },
  }),

  col.accessor((row) => row, {
    id: 'start-date',
    header: 'Start date',
    cell: ({ getValue }) => {
      const { classTime, overrideStartPlainDate } = getValue();
      const displayStartDate = overrideStartPlainDate ?? classTime.schedule.startPlainDate;
      return <span>{formatPlainDate(displayStartDate)}</span>;
    },
  }),

  col.accessor((row) => row, {
    id: 'end-date',
    header: 'End date',
    cell: ({ getValue }) => {
      const { classTime, overrideEndPlainDate } = getValue();
      const displayEndDate = overrideEndPlainDate ?? classTime.schedule.endPlainDate;
      return <span>{formatPlainDate(displayEndDate)}</span>;
    },
  }),

  col.accessor('classTime', {
    id: 'class-time',
    header: 'Class time (CT)',
    cell: ({ getValue }) => {
      const classTime = getValue();
      const schoolTimeZone = classTime.schedule.school.org.timeZone;
      return (
        <Badge variant="outline" className="text-nowrap">
          {formatStudentClassTimeAbbr(
            adjustMinimalClassTimeAcrossTZ(classTime, schoolTimeZone, FrequentTimeZones.UsCentral),
          )}
        </Badge>
      );
    },
  }),

  col.accessor('learningPath', {
    id: 'learningPath',
    header: 'Learning path',
    enableSorting: false,
    cell: ({ getValue }) => getValue().name,
  }),

  col.accessor('startingLesson', {
    id: 'startingLesson',
    header: 'Starting lesson',
    enableSorting: false,
    cell: ({ getValue }) => getValue().name,
  }),

  col.accessor('nextLesson', {
    id: 'nextLesson',
    header: 'Next lesson',
    cell: ({ getValue }) => getValue()?.name ?? '-',
  }),

  col.accessor('futureSessions', {
    id: 'futureSessions',
    header: 'Number of future sessions',
    cell: ({ getValue }) => getValue() ?? '-',
  }),

  col.accessor('studentCount', {
    id: 'studentCount',
    header: 'Number of students',
  }),

  col.accessor((row) => row, {
    id: 'createdAt',
    header: 'Created time (CT)',
    cell: ({ getValue }) => {
      const { createdAt } = getValue();
      const { formattedDateTime } = formatInstant(
        safeInstantFromString(String(createdAt)),
        FrequentTimeZones.UsCentral,
      );
      return <span>{formattedDateTime}</span>;
    },
  }),

  col.accessor((row) => row, {
    id: 'updatedAt',
    header: 'Last updated time (CT)',
    cell: ({ getValue }) => {
      const { updatedAt } = getValue();
      const { formattedDateTime } = formatInstant(
        safeInstantFromString(String(updatedAt)),
        FrequentTimeZones.UsCentral,
      );
      return <span>{formattedDateTime}</span>;
    },
  }),
];

function StudentGroupsIndex() {
  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[nameQuery, activeNameQuery], setNameQuery],
      scheduleSchools: [[selectedSchools, activeSelectedSchools], setSelectedSchools],
      scheduleOrgs: [[selectedOrgs, activeSelectedOrgs], setSelectedOrgs],
      grades: [[selectedGrades, activeSelectedGrades], setSelectedGrades],
      types: [[selectedTypes, activeSelectedTypes], setSelectedTypes],
    },
    resetFilters,
  } = usePaginatedFilters(Route.id, ['query', 'scheduleSchools', 'scheduleOrgs', 'grades', 'types']);
  const [sortByFromSearch, didChangeSortBy] = useNavigateSearchState(Route.id, 'sortBy');
  const effectiveSortBy = sortByFromSearch ?? ['school-asc'];
  const { sorting, ...tableSortingOptions } = useSorting<GetStudentGroupsSortableColumn>([
    effectiveSortBy,
    didChangeSortBy,
  ]);

  const allOrganizationOptions = useOrgOptions();
  const allSchoolOptions = useSchoolAndOrgOptions({
    orgIds: activeSelectedOrgs ?? [],
  });

  const {
    data: response,
    isPending,
    error,
    refetch,
  } = useQuery(
    getStudentGroups({
      pageIndex,
      pageSize,
      filters: {
        query: activeNameQuery,
        scheduleSchools: activeSelectedSchools,
        scheduleOrgs: activeSelectedOrgs,
        grades: activeSelectedGrades,
        types: activeSelectedTypes,
        statuses: [StudentGroupStatus.active],
      },
      sortBy: effectiveSortBy,
    }),
  );

  const studentGroups = useMemo(() => response?.studentGroups ?? EmptyArray, [response]);
  useErrorToast(error, 'Error loading student groups. Please try again.');

  const { rowSelection, rowDataSelection, onRowSelectionChange, getRowId, resetAllRowSelection } =
    useRowsBulkSelection<StudentGroupWithScheduleAndLevel>({
      getRowId: (row) => row.id,
      rowIdToData: (id) => studentGroups.find((studentGroup) => studentGroup.id === id),
    });

  const table = useReactTable({
    data: studentGroups,
    columns,
    state: {
      pagination: {
        pageIndex,
        pageSize,
      },
      sorting,
      rowSelection,
    },
    rowCount: response?.total ?? 0,
    getCoreRowModel: getCoreRowModel(),
    getRowId,
    onPaginationChange,
    onRowSelectionChange,
    manualPagination: true,
    ...tableSortingOptions,
    enableColumnResizing: true,
  });

  const filterLeftComponents = useMemo(
    () => (
      <>
        <TableFilterInputField
          placeholder="Search by students, teachers, class ID"
          value={nameQuery ?? ''}
          onChange={(event) => {
            setNameQuery(event.target.value);
          }}
        />
        <TableFilterDropdown
          title="School"
          options={allSchoolOptions}
          selectedValues={selectedSchools ?? []}
          onChange={setSelectedSchools}
        />
        <TableFilterDropdown
          title="District"
          options={allOrganizationOptions}
          selectedValues={selectedOrgs ?? []}
          onChange={setSelectedOrgs}
        />
        <TableFilterDropdown
          title="Grade"
          options={getStudentYearOptions()}
          selectedValues={selectedGrades ?? []}
          onChange={setSelectedGrades}
        />
        <TableFilterDropdown
          title="Type"
          options={studentGroupTypesFilterOption}
          selectedValues={selectedTypes ?? []}
          onChange={setSelectedTypes}
        />
        <TableFilterResetButton show onClick={resetFilters} />
      </>
    ),
    [
      nameQuery,
      setNameQuery,
      allSchoolOptions,
      selectedSchools,
      setSelectedSchools,
      allOrganizationOptions,
      selectedOrgs,
      setSelectedOrgs,
      selectedGrades,
      setSelectedGrades,
      selectedTypes,
      setSelectedTypes,
      resetFilters,
    ],
  );

  const bulkActionsMenuItems = useMemo(
    () => (
      <>
        <BulkUpdateStudentGroups
          studentGroups={rowDataSelection}
          bulkType={EditStudentGroupBulkActions.ArchiveSessions}
          title="Archive student groups"
          confirmMessage="Are you sure you want to archive"
          invalidMessage="Only active student groups can be selected."
          onSuccess={resetAllRowSelection}
        />
        <DeleteStudentGroup
          studentGroups={rowDataSelection}
          triggerTestId="student-groups-bulk-actions-button-delete"
          dataTestId="student-groups-modal-delete"
          afterDelete={resetAllRowSelection}
        />
      </>
    ),
    [resetAllRowSelection, rowDataSelection],
  );

  return (
    <InfoPage headerText="Student groups" tabs={studentGroupsTabs()}>
      <EntityTable
        table={table}
        showPagination={true}
        isLoading={isPending}
        hasError={!!error}
        emptyState="No student groups found"
        errorState={<TableErrorState refetchAction={refetch} />}
        bulkActionsMenuItems={bulkActionsMenuItems}
        filters={{ filterLeftComponents }}
        autoManagedColumnIds={['selection', 'reassignment-request']}
      />
    </InfoPage>
  );
}
